/* eslint-disable no-console */
import * as Sentry from '@sentry/nextjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useEffectOnce } from 'react-use'
import { getEnv } from '../../../config'
import { getCookieByName } from '../../../lib/cookies'
import { off, on, trigger } from '../../../lib/events'
import { FirebaseAuth } from '../../../lib/firebase'
import { moisesSDK } from '../../../lib/moises-api'
import { UserToken } from '../../../types/user-context.types'
import { useSerializedCallback } from '../../misc/use-serialized-callback'

// Enhanced interface for Firebase token management
export interface UseFirebaseTokenReturn {
  userToken: UserToken
  getValidToken: () => Promise<UserToken>
  forceRefresh: () => Promise<void>
  isRefreshing: boolean
}

// for debug
if (typeof window !== 'undefined') {
  window.FirebaseAuth = FirebaseAuth
}

const delayRefreshOnRecentlyCreatedAccount = async (): Promise<void> => {
  const user = FirebaseAuth.currentUser

  if (!user || !user.metadata.creationTime) {
    return
  }

  const isOnlyPassword =
    user.providerData.length === 1 &&
    user.providerData[0].providerId === 'password'

  const accountCreationTime = new Date(user.metadata.creationTime).getTime()
  const accountRecentlyCreated = Date.now() - accountCreationTime < 5000

  if (isOnlyPassword && accountRecentlyCreated) {
    await new Promise((resolve) => setTimeout(resolve, 3000))
  }
}

const getMusicFirstToken = (): string | null => {
  const token = getCookieByName(`musicFirstToken-${getEnv()}`)
  if (token) {
    return `MFIRST-${token}`
  }
  return null
}

const useFrameToken = (): {
  userFrameToken: string | null
  setUserFrameToken: (token: string) => void
} => {
  const isBrowser = typeof window !== 'undefined'

  const [userFrameToken, updateUserFrameToken] = useState<string | null>(
    isBrowser
      ? window.sessionStorage.getItem('moises:user:token') ||
          getMusicFirstToken() ||
          null
      : null
  )

  const setUserFrameToken = useCallback((token: string) => {
    window.sessionStorage.setItem('moises:user:token', token)
    updateUserFrameToken(token)
  }, [])

  return {
    userFrameToken,
    setUserFrameToken
  }
}

const notifyAllFrames = (newToken: string | null): void => {
  const iframes = [
    ...global.window.document.querySelectorAll('iframe').values()
  ]

  iframes
    .filter((frame) => frame.src)
    .map((frame) => ({ url: new URL(frame.src), frame }))
    .filter(({ url }) => url.hostname.match(/moises\.ai$/))
    .forEach(({ frame, url }) => {
      if (frame.contentWindow === null) return
      frame.contentWindow.postMessage(
        {
          type: 'refresh-token',
          payload: newToken
        },
        `${url.protocol}//${url.hostname}${url.port ? `:${url.port}` : ''}`
      )
    })
}

export const useFirebaseToken = (): UseFirebaseTokenReturn => {
  const { userFrameToken, setUserFrameToken } = useFrameToken()
  const userTokenRef = useRef<UserToken>(null)
  const [userToken, setUserToken] = useState<UserToken>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const setNewUserToken = useCallback((newToken: UserToken) => {
    if (newToken !== userTokenRef.current) {
      console.log(newToken ? 'User tkn changed' : 'User tkn removed')
      userTokenRef.current = newToken

      if (newToken) {
        moisesSDK.auth.updateFirebaseToken(newToken)
      }

      setUserToken(newToken)
      trigger('firebase:token-changed', newToken)
    }
    notifyAllFrames(newToken)
  }, [])

  const wrappedGetIdToken = useCallback(
    async (forceRefresh = false) => {
      // If we have a frame token, no need to refresh (external auth that doesn't expire)
      if (userFrameToken) {
        return
      }

      if (!FirebaseAuth.currentUser) {
        console.warn('Refresh firebase tkn unavailable: No user')
        return
      }

      setIsRefreshing(true)

      if (forceRefresh) {
        console.log('Forcing firebase tkn refresh')
        await delayRefreshOnRecentlyCreatedAccount()
      }

      try {
        const token = await FirebaseAuth.currentUser.getIdToken(forceRefresh)
        setNewUserToken(token)
      } catch (error: any) {
        console.error('Refresh firebase tkn:', error)
        Sentry.captureMessage('Firebase: Refresh token', {
          level: 'error',
          extra: { error, code: error.code, customData: error.customData }
        })
        throw error
      } finally {
        setIsRefreshing(false)
      }
    },
    [userFrameToken, setNewUserToken]
  )

  const fetchFirebaseToken = useSerializedCallback(wrappedGetIdToken, [
    wrappedGetIdToken
  ])

  const refreshFirebaseToken = useSerializedCallback(
    () => wrappedGetIdToken(true),
    [wrappedGetIdToken]
  )

  const getValidToken = useCallback(async (): Promise<UserToken> => {
    await fetchFirebaseToken()
    return userTokenRef.current
  }, [fetchFirebaseToken])

  useEffect(() => {
    const unsubscribe = FirebaseAuth.onIdTokenChanged(async (user) => {
      if (user) {
        console.log('Firebase tkn changed: Refreshing tkn')
        fetchFirebaseToken()
      } else if (!userFrameToken) {
        console.log('Firebase tkn changed: User tkn removed')
        setUserToken(null)
      }
    })

    return () => unsubscribe()
  }, [fetchFirebaseToken, userFrameToken])

  useEffectOnce(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', (event) => {
        if (event?.data?.type === 'token-expired') {
          fetchFirebaseToken()
        }
      })
    }

    on('firebase:refresh-token', refreshFirebaseToken)

    const isInsideIframe =
      typeof window !== 'undefined' && window.self !== window.top
    if (isInsideIframe) {
      window.parent.postMessage(
        {
          type: 'moises:frame-ready'
        },
        '*'
      )
    }

    return () => {
      off('firebase:refresh-token', refreshFirebaseToken)
    }
  })

  const handleIframeMessage = useCallback(
    (event: MessageEvent) => {
      if (!event || typeof event.data !== 'string') return

      if (event.data.includes('moises:cleanup-user-token')) {
        window.sessionStorage.removeItem('moises:user:token')
        return
      }

      const hasMoisesToken = event.data.includes('"moisesToken":')
      const hasMoisesUserToken = event.data.includes('moises:user-token')

      if (!hasMoisesToken && !hasMoisesUserToken) return

      try {
        const data = JSON.parse(event.data)
        const rawToken =
          data?.type === 'moises:user-token' ? data?.payload : data?.moisesToken

        if (typeof rawToken !== 'string' || rawToken.length === 0) return

        const token = data.moisesToken ? `API-${rawToken}` : rawToken

        setNewUserToken(token)
        setUserFrameToken(token)
      } catch (error) {
        console.log('Error parsing message:', error)
      }
    },
    [setNewUserToken, setUserFrameToken]
  )

  useEffect(() => {
    if (userFrameToken && !userToken) {
      setNewUserToken(userFrameToken)
    }

    window.addEventListener('message', handleIframeMessage)

    return () => {
      window.removeEventListener('message', handleIframeMessage)
    }
  }, [userToken, userFrameToken, setNewUserToken, handleIframeMessage])

  return {
    userToken,
    getValidToken,
    forceRefresh: refreshFirebaseToken,
    isRefreshing
  }
}
